// src/pages/ELearning/BenefitConstructor.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBenefitById, createBenefit, updateBenefit } from '../../api/client';
import { Save, Loader2, Upload, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

const BenefitConstructor: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [form, setForm] = useState({
    name: '',
    description: '',
    requiredPoints: 0,
    requiredRating: 0,
    image: '',
    expiresAt: ''
  });
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      const fetchBenefit = async () => {
        setLoading(true);
        try {
          const data = await getBenefitById(id);
          setForm({
            name: data.name,
            description: data.description || '',
            requiredPoints: data.requiredPoints,
            requiredRating: data.requiredRating,
            image: data.image || '',
            expiresAt: data.expiresAt ? data.expiresAt.split('T')[0] : ''
          });
          setImagePreview(data.image || '');
        } catch (err: any) {
          setError(err.response?.data?.error || 'Не удалось загрузить бенефит');
        } finally {
          setLoading(false);
        }
      };
      fetchBenefit();
    }
  }, [id]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setForm({ ...form, image: base64 });
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      showToast('Введите название бенефита', 'error');
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await updateBenefit(id, form);
        showToast('Бенефит обновлён', 'success');
      } else {
        await createBenefit(form);
        showToast('Бенефит создан', 'success');
      }
      navigate('/app/elearning/benefits');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error}</h2>
          <button onClick={() => navigate('/app/elearning/benefits')} style={styles.retryButton}>
            Вернуться к бенефитам
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .bc-hero { animation: fadeUp 0.5s ease-out; }
        .bc-section { animation: fadeUp 0.5s ease-out backwards; animation-delay: 0.1s; }
        .bc-input:focus { border-color: transparent !important; box-shadow: 0 0 0 2px #f5576c !important; }
        .bc-btn-primary { transition: all 0.3s ease; }
        .bc-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(245, 87, 108, 0.4) !important; }
        .bc-upload:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(245, 87, 108, 0.35) !important; }
      `}</style>

      <div style={styles.container}>
        {/* ═══ Hero ═══ */}
        <div className="bc-hero" style={styles.hero}>
          <div style={styles.heroIcon}>
            <Save size={28} color="#fff" />
          </div>
          <div>
            <h1 style={styles.title}>
              {id ? 'Редактировать бенефит' : 'Новый бенефит'}
            </h1>
            <p style={styles.subtitle}>
              {id ? 'Обновите параметры приза' : 'Создайте новый приз для сотрудников'}
            </p>
          </div>
        </div>

        {/* ═══ Секция 1: Основное ═══ */}
        <div className="bc-section" style={styles.section}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>1</span>
            <h2 style={styles.sectionTitle}>Основное</h2>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Название *</label>
            <input
              className="bc-input"
              type="text"
              placeholder="Например: Дополнительный выходной"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Описание</label>
            <textarea
              className="bc-input"
              placeholder="Краткое описание бенефита"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              style={styles.textarea}
            />
          </div>
        </div>

        {/* ═══ Секция 2: Условия ═══ */}
        <div className="bc-section" style={{ ...styles.section, animationDelay: '0.15s' }}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>2</span>
            <h2 style={styles.sectionTitle}>Условия получения</h2>
          </div>

          <div style={styles.grid2}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Требуется баллов</label>
              <input
                className="bc-input"
                type="number"
                placeholder="0"
                value={form.requiredPoints}
                onChange={e => setForm({ ...form, requiredPoints: +e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Требуется рейтинг</label>
              <input
                className="bc-input"
                type="number"
                placeholder="0"
                value={form.requiredRating}
                onChange={e => setForm({ ...form, requiredRating: +e.target.value })}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Действует до</label>
            <input
              className="bc-input"
              type="date"
              value={form.expiresAt}
              onChange={e => setForm({ ...form, expiresAt: e.target.value })}
              style={styles.input}
            />
          </div>
        </div>

        {/* ═══ Секция 3: Изображение ═══ */}
        <div className="bc-section" style={{ ...styles.section, animationDelay: '0.2s' }}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>3</span>
            <h2 style={styles.sectionTitle}>Изображение</h2>
          </div>

          <div style={styles.imageUpload}>
            {imagePreview ? (
              <img src={imagePreview} alt="preview" style={styles.preview} />
            ) : (
              <div style={styles.imagePlaceholder}>
                <ImageIcon size={32} color="#8b8fa8" />
              </div>
            )}
            <label className="bc-upload" style={styles.uploadLabel}>
              <Upload size={16} /> {imagePreview ? 'Заменить' : 'Загрузить картинку'}
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        {/* ═══ Actions ═══ */}
        <div style={styles.actions}>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="bc-btn-primary"
            style={styles.saveButton}
          >
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={() => navigate('/app/elearning/benefits')} style={styles.cancelButton}>
            Отмена
          </button>
        </div>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 720, margin: '0 auto' },
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },
  section: { background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', border: '1px solid rgba(255, 255, 255, 0.7)', borderRadius: 24, padding: 24, marginBottom: 20, boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  sectionBadge: { width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)' },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' },
  formGroup: { marginBottom: 16 },
  label: { display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#5a5f7a' },
  input: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, minHeight: 90, outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },
  imageUpload: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  preview: { width: 120, height: 120, objectFit: 'cover', borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' },
  imagePlaceholder: { width: 120, height: 120, background: 'rgba(0,0,0,0.04)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed rgba(245,87,108,0.3)' },
  uploadLabel: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', borderRadius: 14, cursor: 'pointer', fontWeight: 700, fontSize: 14, boxShadow: '0 8px 20px rgba(245, 87, 108, 0.3)', transition: 'all 0.3s ease' },
  actions: { display: 'flex', gap: 12, marginTop: 20, marginBottom: 40 },
  saveButton: { background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, boxShadow: '0 8px 24px rgba(245, 87, 108, 0.35)' },
  cancelButton: { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '14px 28px', cursor: 'pointer', fontWeight: 600, fontSize: 15, color: '#5a5f7a' },
  retryButton: { marginTop: 20, padding: '10px 24px', background: '#2575fc', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 }
};

export default BenefitConstructor;